import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatApiError } from "../../lib/api";
import {
  deleteAllFirmware,
  downloadFirmwareXlsx,
  previewFirmwareDeleteAll,
} from "../../lib/firmwareApi";

export const FIRMWARE_DELETE_ALL_CONFIRMATION = "DELETE ALL FIRMWARE";

function firmwareDeleteError(error) {
  const detail = error?.response?.data?.detail;
  return detail?.message || formatApiError(error);
}

export default function FirmwareDeleteAllDialog({ open, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const requestVersion = useRef(0);
  const inFlight = useRef(false);

  async function loadPreview() {
    if (inFlight.current) return;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    inFlight.current = true;
    setLoading(true);
    setError("");
    try {
      const result = await previewFirmwareDeleteAll();
      if (requestVersion.current === version) setPreview(result);
    } catch (requestError) {
      if (requestVersion.current === version) {
        setError(firmwareDeleteError(requestError));
      }
    } finally {
      if (requestVersion.current === version) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!open) {
      requestVersion.current += 1;
      inFlight.current = false;
      return;
    }
    setPreview(null);
    setConfirmation("");
    setError("");
    setDeleting(false);
    inFlight.current = false;
    loadPreview();
    // The open transition is the only trigger; retry is explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  function close() {
    if (inFlight.current || deleting) return;
    requestVersion.current += 1;
    onClose();
  }

  async function confirmDelete() {
    if (
      inFlight.current ||
      !preview?.snapshot_hash ||
      confirmation !== FIRMWARE_DELETE_ALL_CONFIRMATION
    ) {
      return;
    }
    inFlight.current = true;
    setDeleting(true);
    setError("");
    try {
      const backup = await deleteAllFirmware({
        confirmation,
        snapshot_hash: preview.snapshot_hash,
      });
      downloadFirmwareXlsx(backup);
      toast.success(
        `${backup.deletedCount || preview.count || 0} Firmware records deleted. Backup downloaded.`
      );
      onDeleted?.();
      onClose();
    } catch (requestError) {
      setError(firmwareDeleteError(requestError));
    } finally {
      inFlight.current = false;
      setDeleting(false);
    }
  }

  const confirmed = confirmation === FIRMWARE_DELETE_ALL_CONFIRMATION;
  return (
    <Modal
      title="Delete All Firmware"
      onClose={close}
      busy={loading || deleting}
      size="sm"
      testid="firmware-delete-all-dialog"
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <AlertTriangle className="mt-0.5 text-destructive" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-destructive">Permanent bulk deletion</p>
            <p className="mt-1 text-xs text-muted-foreground">
              A backup XLSX is generated from the protected snapshot before deletion.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground" role="status">
            Loading Firmware snapshot…
          </p>
        ) : preview ? (
          <p className="text-sm" data-testid="firmware-delete-all-count">
            Current records: <span className="font-semibold tabular-nums">{preview.count || 0}</span>
          </p>
        ) : null}

        <label className="block">
          <span className="mb-1.5 block text-xs font-medium">
            Type {FIRMWARE_DELETE_ALL_CONFIRMATION} to confirm
          </span>
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={loading || deleting || !preview}
            autoComplete="off"
            data-testid="firmware-delete-all-confirmation"
          />
        </label>

        {error ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive" role="alert" data-testid="firmware-delete-all-error">
              {error}
            </p>
            {!preview ? (
              <Button type="button" variant="retry" size="sm" onClick={loadPreview}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button type="button" variant="cancel" onClick={close} disabled={loading || deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={confirmDelete}
            disabled={!preview || !confirmed || loading}
            loading={deleting}
            loadingLabel="Deleting…"
            data-action-category="destructive"
            data-testid="firmware-delete-all-submit"
          >
            <Trash2 aria-hidden="true" />
            Delete All and Download Backup
          </Button>
        </div>
      </div>
    </Modal>
  );
}
